import Commons from "oc-commons/Commons.js";

const maxRecursionCount = 100;
let logAllEvents = false;

/**
 * Fügt einer Klasse per ES6-Mixin Listener Fähigkeit hinzu,
 * welche derselben Syntax wie jQuerys Eventsystem folgen.
 *
 * @template {{new (...args: any[]) => {}} Super
 * @param {Super} superClass
 *
 * @example
 * const dummy = new (ListenerMixin(THREE.Objekt3D))();
 * // Das Objekt hat daraufhin folgende zusätzliche Methoden:
 * dummy.on('feuer',()=>console.log('es brennt!'));
 * dummy.on('nett',()=>console.log('wie schön!'));
 * dummy.trigger('feuer');
 * dummy.off('feuer');
 *
 */
const ListenerMixin = (superClass=class {}) =>
    class ListenerMix extends superClass {

        /** @type {Object} */
        _listeners;
        /** @type {number} */
        _recursionCounter;

        constructor(...args) {
            super(...args);
            this._listeners = {};
            this._recursionCounter = 0;
            this._logEvents = false;
        }

        _ensureListeners(name) {
            if (this._listeners[name]) {
                return;
            }
            this._listeners[name] = {
                name: name,
                list: [],
                listOnce: [],
            };
        }

        _callThem(name, list, args) {
            if (this._recursionCounter>maxRecursionCount) {
                throw 'trigger("'+name+'") exceeds '+maxRecursionCount+' recursions (might lead to stackoverflow)';
            }
            try {
                this._recursionCounter++;
                const length = list.length;
                for (let i=0;i<length;i++) {
                    list[i].apply(this, args);
                }
            } finally {
                this._recursionCounter--;
            }
        }

        /**
         * ruft alle Listener zu einem Namen auf mit optionalen Parametern
         */
        trigger(name,...args) {
            if (this._logEvents || logAllEvents) {
                console.log('event: '+(this.name?this.name:Commons.typeName(this))+'.'+name);
            }
            // console.log(Commons.typeName(this)+'.trigger(\''+name+'\','+args.join(',')+')');
            this._ensureListeners(name);
            const _listeners = this._listeners[name];
            this._callThem(name,_listeners.list,args);
            this._callThem(name,_listeners.listOnce,args);
            if (_listeners.listOnce.length>0) {
                _listeners.listOnce = [];
            }
            return this;
        }

        /**
         * registriert einen Listener, der immer aufgerufen wird
         * @param name der Name des Events
         * @param callback der zu registrierende Callback / Listener
         * @param cleanUpList optional: falls übergeben, ein Array, in das eine Cleanup-Methode gepushed wird, welche diesen Listener wieder abräumt.
         */
        on(name, callback, cleanUpList = undefined) {
            if (!_.isFunction(callback)) {
                throw "callback is not a function";
            }
            this._ensureListeners(name);
            this._listeners[name].list.push(callback);
            if (cleanUpList) {
                cleanUpList.push(()=>this.off(name,callback));
            }
            return this;
        }

        /**
         * registriert einen Listener, der genau ein Mal aufgerufen wird
         * @param name der Name des Events
         * @param callback der zu registrierende Callback / Listener
         * @param cleanUpList optional: falls übergeben, ein Array, in das eine Cleanup-Methode gepushed wird, welche diesen Listener wieder abräumt.
         */
        onOnce(name, callback, cleanUpList = undefined) {
            if (!_.isFunction(callback)) {
                throw "callback is not a function";
            }
            this._ensureListeners(name);
            this._listeners[name].listOnce.push(callback);
            return this;
        }

        /**
         * deregistriert einen Listener (nur solche, die immer aufgerufen würden)
         * @param name der Name des Events
         * @param callback der zu registrierende Callback / Listener
         */
        off(name, callback) {
            if (this._listeners && this._listeners[name]) {
                if (callback) {
                    if (!_.isFunction(callback)) {
                        throw "callback is not a function";
                    }
                    _.remove(this._listeners[name].list, x=>x===callback);
                } else {
                    delete this._listeners[name];
                }
            }
            return this;
        }

        /**
         * deregistriert einen Listener (nur solche, die nur einmal aufgerufen werden)
         * @param name der Name des Events
         * @param callback der zu registrierende Callback / Listener
         */
        offOnce(name, callback) {
            if (this._listeners && this._listeners[name]) {
                if (callback) {
                    if (!_.isFunction(callback)) {
                        throw "callback is not a function";
                    }
                    _.remove(this._listeners[name].listOnce, x=>x===callback);
                } else {
                    delete this._listeners[name];
                }
            }
            return this;
        }

    }


export default ListenerMixin;
